import { createAsyncThunk } from '@reduxjs/toolkit'
import { GetLocationListByTypeResponseType, LocationType } from 'features/Location/@types/location'
import { getLocationListByType } from 'features/Location/services/location'
import {
  getPurchaseOrderApi,
  getPurchaseOrderReasonsApi,
  postCancelPurchaseOrderApi,
  putUpdateStatusPurchaseOrderApi,
  PutUpdateStatusPurchaseOrderRequestPayloadInterface,
  type GetPurchaseOrderRequestParamsInterface,
  type GetPurchaseOrderReasonsRequestParamsInterface,
  type PostCancelPurchaseOrderPayloadInterface,
} from 'features/PurchaseOrder/services/purchaseOrder'
import { type ReasonType } from 'features/PurchaseOrder/@types/PurchaseOrder'
import { callErrorMsg } from 'helpers/errorMsg'
import { listAccessForOpenAllPage } from 'middleware/privateRoute'
import { locationTypes } from 'features/Location/constants/location'
import { AuthInitialStateType } from 'features/Auth/redux/Auth'
import { toastSuccess } from 'utils/toast'
import {
  getVendorsApi,
  type GetVendorsRequestParamsType,
} from 'features/PurchaseOrder/services/vendor'

export const SLICE_NAME_PURCHASE_ORDER = 'PURCHASE_ORDER'

const getFilteredUserLocationByType = ({
  locations,
  locationFilterTypes,
}: {
  locations: AuthInitialStateType['userData']['user_locations']
  locationFilterTypes: LocationType[]
}) => {
  const allLocationTypes = Object.entries(locationTypes).map(([_, type]) => type)
  const allowedLocationTypes =
    locationFilterTypes.length > 0 ? locationFilterTypes : allLocationTypes

  return locations.filter((location) => allowedLocationTypes.includes(location.location_type))
}

export const fetchPurchaseOrderFilterLocations = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER}/fetchPurchaseOrderFilterLocations`,
  async (params: { locationTypes: LocationType[] }, { rejectWithValue, getState }) => {
    const {
      auth: { userData },
    } = getState() as StoreStateType

    const isUserAbleToAccessAllLocations = userData.authorities.some((auth) =>
      listAccessForOpenAllPage.includes(auth.name),
    )

    try {
      if (isUserAbleToAccessAllLocations) {
        const locationTypeParams = params.locationTypes.length
          ? params.locationTypes.map((type) => type.toLowerCase())
          : ['all']

        const response = await Promise.all(
          locationTypeParams.map((type) => getLocationListByType(type)),
        )
        return response.reduce<GetLocationListByTypeResponseType[]>((acc, res) => {
          if (res.data) {
            return acc.concat(res.data)
          }
          return acc
        }, [])
      }

      const filteredUserLocationByType = getFilteredUserLocationByType({
        locations: userData.user_locations,
        locationFilterTypes: params.locationTypes,
      })

      return filteredUserLocationByType.map((location) => ({
        location_id: location.location_id,
        location_name: location.location_name,
        location_type: location.location_type,
      }))
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchPurchaseOrderList = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER}/fetchPurchaseOrderList`,
  async (
    {
      queryParams,
      RBACPageID,
    }: { queryParams: GetPurchaseOrderRequestParamsInterface; RBACPageID?: string },
    { rejectWithValue, getState },
  ) => {
    const {
      auth: { userData },
    } = getState() as StoreStateType
    const isUserAbleToAccessAllLocations = userData.authorities.some((auth) =>
      listAccessForOpenAllPage.includes(auth.name),
    )

    try {
      const updatedQueryParams = { ...queryParams }

      // set default destination based on user auth locations
      if (!queryParams.destination_ids?.length && !isUserAbleToAccessAllLocations) {
        const filteredUserLocationByType = getFilteredUserLocationByType({
          locations: userData.user_locations,
          locationFilterTypes: queryParams.location_types || [],
        })

        updatedQueryParams.destination_ids = filteredUserLocationByType.map(
          (location) => location.location_id,
        )
      }

      const { data } = await getPurchaseOrderApi(updatedQueryParams, RBACPageID)

      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const updatePurchaseOrderStatus = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER}/updatePurchaseOrderStatus`,
  async (
    {
      purchaseOrderId,
      payload,
    }: { purchaseOrderId: number; payload: PutUpdateStatusPurchaseOrderRequestPayloadInterface },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await putUpdateStatusPurchaseOrderApi(purchaseOrderId, payload)

      toastSuccess(data.message || 'Purchase order status berhasil diupdate.')

      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchPurchaseOrderCancelReasons = createAsyncThunk<
  ReasonType[],
  GetPurchaseOrderReasonsRequestParamsInterface
>(`${SLICE_NAME_PURCHASE_ORDER}/fetchPurchaseOrderCancelReasons`, async (params) => {
  try {
    const { data } = await getPurchaseOrderReasonsApi(params)
    return data.data
  } catch (error) {
    callErrorMsg(error)
    return []
  }
})

export const cancelPurchaseOrder = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER}/cancelPurchaseOrder`,
  async (
    {
      purchaseOrderId,
      payload,
    }: { purchaseOrderId: number; payload: PostCancelPurchaseOrderPayloadInterface },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await postCancelPurchaseOrderApi(purchaseOrderId, payload)
      toastSuccess(data.message || 'Purchase order berhasil dibatalkan.')
      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchVendors = createAsyncThunk(
  `${SLICE_NAME_PURCHASE_ORDER}/fetchVendors`,
  async (params: GetVendorsRequestParamsType, { rejectWithValue }) => {
    try {
      const { data } = await getVendorsApi(params)
      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)
